import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore, useThemeStore, useLanguageStore } from "../store";
import {
  FiLogOut,
  FiMoon,
  FiSun,
  FiMenu,
  FiX,
  FiUser,
  FiHome,
  FiGrid,
  FiSettings,
  FiAward,
  FiCreditCard,
  FiList,
  FiPlusCircle,
} from "react-icons/fi";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuthStore((state) => ({
    user: state.user,
    logout: state.logout,
  }));
  const { isDark, toggleTheme } = useThemeStore();
  const { language, setLanguage } = useLanguageStore();
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showSectionNav, setShowSectionNav] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Detectar scroll para cambiar el header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Detectar scroll y movimiento del mouse para el navegador de secciones
  useEffect(() => {
    if (location.pathname !== "/") return;

    let scrollTimeout;
    let isUserScrolling = false;

    const handleScroll = () => {
      isUserScrolling = true;
      setIsScrolling(true);

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isUserScrolling = false;
        setIsScrolling(false);
      }, 1500); // Ocultar después de 1.5 segundos sin scroll
    };

    const handleMouseMove = (e) => {
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;
      const threshold = 100; // Mostrar cuando el mouse está a 100px del borde derecho

      if (windowWidth - mouseX < threshold) {
        setShowSectionNav(true);
      } else if (!isUserScrolling) {
        setShowSectionNav(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
      clearTimeout(scrollTimeout);
    };
  }, [location.pathname]);

  // Detectar la sección activa en la página de inicio
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null);
      return;
    }

    const handleScroll = () => {
      const sections = [
        { id: "hero", element: document.querySelector(".home-hero") },
        {
          id: "features",
          element: document
            .querySelector(".home-features-grid")
            ?.closest(".home-section"),
        },
        { id: "stats", element: document.querySelector(".home-stats") },
        {
          id: "services",
          element: document
            .querySelector(".home-services-grid")
            ?.closest(".home-section"),
        },
        { id: "values", element: document.querySelector(".home-about") },
        { id: "cta", element: document.querySelector(".home-cta") },
      ];

      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element) {
          const sectionTop = section.element.offsetTop;
          if (scrollPosition >= sectionTop) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Ejecutar al montar
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setSideMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const handleThemeToggle = () => {
    toggleTheme();
  };

  return (
    <>
      {/* Header Principal */}
      <header className={`modern-header ${scrolled ? "scrolled" : ""}`}>
        <div className="modern-header-container">
          {/* Botón del menú lateral */}
          <button
            onClick={() => setSideMenuOpen(!sideMenuOpen)}
            className="modern-header-menu-button"
            aria-label="Toggle menu"
          >
            <FiMenu size={24} />
          </button>

          {/* Logo central */}
          <div onClick={() => navigate("/")} className="modern-header-logo">
            <span className="modern-header-logo-text">
              {t("common.appName")}
            </span>
          </div>

          {/* Acciones de la derecha */}
          <div className="modern-header-actions">
            {/* Language Selector */}
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="modern-header-language"
            >
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="fr">FR</option>
              <option value="de">DE</option>
            </select>

            {/* Theme Toggle */}
            <button
              onClick={handleThemeToggle}
              className="modern-header-theme-button"
              aria-label="Toggle theme"
            >
              {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Overlay cuando el menú está abierto */}
      {sideMenuOpen && (
        <div
          className="side-menu-overlay"
          onClick={() => setSideMenuOpen(false)}
        />
      )}

      {/* Menú Lateral */}
      <nav className={`side-menu ${sideMenuOpen ? "open" : ""}`}>
        <div className="side-menu-header">
          <div className="side-menu-logo">
            <div className="side-menu-logo-icon">AI</div>
            <span className="side-menu-logo-text">{t("common.appName")}</span>
          </div>
          <button
            onClick={() => setSideMenuOpen(false)}
            className="side-menu-close"
            aria-label="Close menu"
          >
            <FiX size={24} />
          </button>
        </div>

        <div className="side-menu-content">
          {/* Navegación principal */}
          <div className="side-menu-section">
            <button
              onClick={() => navigate("/")}
              className={`side-menu-item ${isActive("/") ? "active" : ""}`}
            >
              <FiHome className="side-menu-item-icon" />
              <span>{t("nav.home")}</span>
            </button>

            {user && (
              <>
                <button
                  onClick={() => navigate("/dashboard")}
                  className={`side-menu-item ${
                    isActive("/dashboard") ? "active" : ""
                  }`}
                >
                  <FiGrid className="side-menu-item-icon" />
                  <span>{t("nav.dashboard")}</span>
                </button>

                <button
                  onClick={() => navigate("/interviews/create")}
                  className={`side-menu-item ${
                    isActive("/interviews/create") ? "active" : ""
                  }`}
                >
                  <FiPlusCircle className="side-menu-item-icon" />
                  <span>{t("interview.newInterview")}</span>
                </button>

                <button
                  onClick={() => navigate("/interviews")}
                  className={`side-menu-item ${
                    isActive("/interviews") ? "active" : ""
                  }`}
                >
                  <FiList className="side-menu-item-icon" />
                  <span>{t("common.interviews")}</span>
                </button>

                <button
                  onClick={() => navigate("/subscription")}
                  className={`side-menu-item ${
                    isActive("/subscription") ? "active" : ""
                  }`}
                >
                  <FiCreditCard className="side-menu-item-icon" />
                  <span>{t("subscription.title")}</span>
                </button>
              </>
            )}
          </div>

          {/* Sección de usuario o botones de auth */}
          <div className="side-menu-section">
            <div className="side-menu-divider" />

            {user ? (
              <>
                <button
                  onClick={() => navigate("/settings")}
                  className={`side-menu-item ${
                    isActive("/settings") ? "active" : ""
                  }`}
                >
                  <FiSettings className="side-menu-item-icon" />
                  <span>{t("settings.title")}</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="side-menu-item logout"
                >
                  <FiLogOut className="side-menu-item-icon" />
                  <span>{t("common.logout")}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className={`side-menu-item ${
                    isActive("/login") ? "active" : ""
                  }`}
                >
                  <FiUser className="side-menu-item-icon" />
                  <span>{t("common.login")}</span>
                </button>

                <button
                  onClick={() => navigate("/register")}
                  className={`side-menu-item ${
                    isActive("/register") ? "active" : ""
                  }`}
                >
                  <FiAward className="side-menu-item-icon" />
                  <span>{t("common.register")}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Información del usuario en la parte inferior */}
        {user && (
          <div className="side-menu-footer">
            <div className="side-menu-user">
              <div className="side-menu-user-avatar">
                <FiUser />
              </div>
              <div className="side-menu-user-info">
                <div className="side-menu-user-name">
                  {user.name || "Usuario"}
                </div>
                <div className="side-menu-user-email">{user.email}</div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Navegador de Secciones (lado derecho) - solo en la página de inicio */}
      {location.pathname === "/" && (
        <div
          className={`section-navigator ${
            showSectionNav || isScrolling ? "scrolling" : ""
          } ${showSectionNav ? "near-edge" : ""}`}
        >
          <div className="section-nav-dots">
            <button
              className={`section-dot ${
                activeSection === "hero" ? "active" : ""
              }`}
              onClick={() =>
                document
                  .querySelector(".home-hero")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Inicio"
            >
              <span className="section-dot-tooltip">Inicio</span>
            </button>
            <button
              className={`section-dot ${
                activeSection === "features" ? "active" : ""
              }`}
              onClick={() =>
                document
                  .querySelector(".home-features-grid")
                  ?.closest(".home-section")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Características"
            >
              <span className="section-dot-tooltip">Características</span>
            </button>
            <button
              className={`section-dot ${
                activeSection === "stats" ? "active" : ""
              }`}
              onClick={() =>
                document
                  .querySelector(".home-stats")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Estadísticas"
            >
              <span className="section-dot-tooltip">Estadísticas</span>
            </button>
            <button
              className={`section-dot ${
                activeSection === "services" ? "active" : ""
              }`}
              onClick={() =>
                document
                  .querySelector(".home-services-grid")
                  ?.closest(".home-section")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Servicios"
            >
              <span className="section-dot-tooltip">Servicios</span>
            </button>
            <button
              className={`section-dot ${
                activeSection === "values" ? "active" : ""
              }`}
              onClick={() =>
                document
                  .querySelector(".home-about")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Valores"
            >
              <span className="section-dot-tooltip">Valores</span>
            </button>
            <button
              className={`section-dot ${
                activeSection === "cta" ? "active" : ""
              }`}
              onClick={() =>
                document
                  .querySelector(".home-cta")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Comenzar"
            >
              <span className="section-dot-tooltip">Comenzar</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
